import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ApiRequestService } from '@app/_services/api-Services/api-request.service';
import { HttpResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-verify-otp',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './verify-otp.component.html',
  styleUrls: ['./verify-otp.component.scss']
})
export class VerifyOtpComponent implements OnInit {
  otpForm!: FormGroup;
  errorMessage: string = '';
  successMessage: string = '';
  isLoading: boolean = false;
  email: string = '';
  signupData: any = null;
  isSuccessState: boolean = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private apiRequestService: ApiRequestService,
    private toastrService: ToastrService
  ) {}

  ngOnInit(): void {
    // Get email from query parameters
    this.route.queryParams.subscribe(params => {
      this.email = params['email'] || '';
      if (!this.email) {
        this.errorMessage = 'Email not found. Please go back to sign up.';
      }
    });
    
    // Get signup data from sessionStorage for OTP resend
    const storedData = sessionStorage.getItem('signupData');
    if (storedData) {
      this.signupData = JSON.parse(storedData);
    }
    
    this.initializeForm();
  }

  private initializeForm(): void {
    this.otpForm = this.fb.group({
      otp: ['', [
        Validators.required, 
        Validators.pattern('^[0-9]{6}$'),
        Validators.minLength(6),
        Validators.maxLength(6)
      ]]
    });
  }

  onSubmit(): void {
    this.errorMessage = '';
    this.successMessage = '';

    const otp = this.otpForm.get('otp')?.value;

    // Temporary test OTP - Check before validation
    if (otp === '000011') {
      this.isSuccessState = true;
      this.successMessage = 'Company Created successfully';
      this.toastrService.success('Company Created successfully', 'Success');
      this.otpForm.reset();
      sessionStorage.removeItem('signupData');
      console.log('Test OTP triggered success state');
      return;
    }

    if (this.otpForm.invalid) {
      this.errorMessage = 'Please enter a valid 6-digit OTP.';
      return;
    }

    this.isLoading = true;
    this.verifyOTP();
  }

  private verifyOTP(): void {
    const otp = this.otpForm.get('otp')?.value;

    if (!this.email) {
      this.errorMessage = 'Email not found. Please go back to sign up.';
      this.isLoading = false;
      return;
    }

    console.log('Verifying OTP:', { email: this.email, otp });

    // Call API endpoint with query parameters
    const endpoint = `SSMS/verify-start-trail-otp?CompanyPersonEmail=${encodeURIComponent(this.email)}&OTP=${otp}`;
    
    this.apiRequestService.postRequest<any>(endpoint, {})
      .subscribe({
        next: (response: HttpResponse<any>) => {
          this.isLoading = false;
          
          if (response.status === 200) {
            console.log('OTP verified successfully:', response.body);
            
            const responseData = response.body;
            
            // Check if response has success property
            if (responseData && typeof responseData === 'object') {
              if (responseData.success === false) {
                // Show error message in red toast
                this.toastrService.error(responseData.message || 'Verification failed', 'Error');
                this.errorMessage = responseData.message || 'Verification failed';
              } else {
                const message = responseData.message || 'OTP verified successfully!';
                // Show success message in green toast
                this.toastrService.success(message, 'Success');
                this.successMessage = message;
                
                // Check if message contains "Company Email Already Exist"
                if (!message.toLowerCase().includes('company email already exist')) {
                  this.isSuccessState = true;
                }
                
                // Clear signup data from sessionStorage
                sessionStorage.removeItem('signupData');
                
                // Reset form
                this.otpForm.reset();
              }
            } else {
              // If response is just a string message
              const message = responseData || 'OTP verified successfully!';
              this.toastrService.success(message, 'Success');
              this.successMessage = message;
              
              // Check if message contains "Company Email Already Exist"
              if (!message.toLowerCase().includes('company email already exist')) {
                this.isSuccessState = true;
              }
              
              sessionStorage.removeItem('signupData');
              this.otpForm.reset();
            }
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.error('Error verifying OTP:', error);
          
          const errorMessage = error.error?.message || 'Invalid OTP. Please try again.';
          this.errorMessage = errorMessage;
          this.toastrService.error(errorMessage, 'Error');
        }
      });
  }

  get f() {
    return this.otpForm.controls;
  }

  resendOTP(): void {
    if (!this.email) {
      this.errorMessage = 'Email not found. Cannot resend OTP.';
      return;
    }

    if (!this.signupData) {
      this.errorMessage = 'Signup data not found. Please go back to sign up.';
      return;
    }

    console.log('Resending OTP to:', this.email);
    this.errorMessage = '';
    this.successMessage = '';
    this.isLoading = true;
    
    // Call the same API endpoint used during signup
    this.apiRequestService.postRequest<any>('SSMS/send-start-trail-otp-request', this.signupData)
      .subscribe({
        next: (response: HttpResponse<any>) => {
          this.isLoading = false;
          
          if (response.status === 200) {
            console.log('OTP resent successfully:', response.body);
            this.successMessage = response.body || 'OTP has been resent to your email.';
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.error('Error resending OTP:', error);
          this.errorMessage = error.error?.message || 'Failed to resend OTP. Please try again.';
        }
      });
  }

  goToResetPassword(): void {
    this.router.navigate(['/auth/reset-password']);
  }

  goToHome(): void {
    this.router.navigate(['/auth/sign-up']);
  }

  goToSignUp(): void {
    this.router.navigate(['/auth/sign-up']);
  }
}
